import { useState, useEffect } from "react";
import {
  Dialog, DialogTitle, DialogContent, IconButton, Box,
  Typography, TextField, Button, Alert, CircularProgress,
  Chip, Divider, InputAdornment, Paper, Grid,
} from "@mui/material";

import CloseIcon         from "@mui/icons-material/Close";
import PaymentIcon       from "@mui/icons-material/Payment";
import TwoWheelerIcon    from "@mui/icons-material/TwoWheeler";
import PersonIcon        from "@mui/icons-material/Person";
import CurrencyRupeeIcon from "@mui/icons-material/CurrencyRupee";
import CheckCircleIcon   from "@mui/icons-material/CheckCircle";
import PhoneIcon         from "@mui/icons-material/Phone";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import ReceiptIcon       from "@mui/icons-material/Receipt";

import api from "../service/api";

// ─── tiny helpers ─────────────────────────────────────────────────────────────
const Label = ({ children }) => (
  <Typography
    variant="caption"
    sx={{ color: "#888", fontWeight: 700, fontSize: "0.62rem", letterSpacing: 0.6, textTransform: "uppercase" }}
  >
    {children}
  </Typography>
);
const Val = ({ children, color }) => (
  <Typography variant="body2" fontWeight={700} color={color || "#111"} mt={0.2}>
    {children || "—"}
  </Typography>
);

// ─── COMPONENT ────────────────────────────────────────────────────────────────
const ExchangeSellModal = ({ open, onClose, bike, onSuccess }) => {

  const [saleDetail,   setSaleDetail]   = useState(null);
  const [fetchLoading, setFetchLoading] = useState(false);
  const [fetchError,   setFetchError]   = useState("");

  const [form,         setForm]         = useState({ amount: "", date: "", name: "", phone: "" });
  const [submitting,   setSubmitting]   = useState(false);
  const [formError,    setFormError]    = useState("");
  const [success,      setSuccess]      = useState(false);
  const [savedPayment, setSavedPayment] = useState(null);

  // ── fetch when modal opens ─────────────────────────────────────────────────
  useEffect(() => {
    if (!open || !bike?.ExchangeSellId) return;

    const load = async () => {
      try {
        setFetchLoading(true);
        setFetchError("");
        setSaleDetail(null);
        setSuccess(false);
        setFormError("");
        setSavedPayment(null);
        setForm({
          amount: "",
          date:   new Date().toISOString().slice(0, 10),
          name:   "",
          phone:  "",
        });

        const res = await api.get(`/specific-bike-details/${bike._id}`);
        const exchangePayment = res.data?.data?.exchangePayment || null;
        setSaleDetail(exchangePayment);
      } catch {
        setFetchError("Failed to load sale details. Please try again.");
      } finally {
        setFetchLoading(false);
      }
    };

    load();
  }, [open, bike]);

  // true if DB already has a payment entry
  const alreadyRecorded =
    saleDetail &&
    (saleDetail.payments?.length > 0 || saleDetail.status === "PAID");

  // ── submit ─────────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    setFormError("");

    if (!form.amount || Number(form.amount) <= 0) { setFormError("Please enter a valid amount."); return; }
    if (!form.date)                                { setFormError("Payment date is required.");    return; }
    if (!form.name.trim())                         { setFormError("Recipient name is required.");  return; }
    if (!form.phone.trim())                        { setFormError("Phone number is required.");    return; }

    try {
      setSubmitting(true);
      const res = await api.post(`/add-exchange-payment/${bike.ExchangeSellId}`, {
        amount: Number(form.amount),
        date:   form.date,
        name:   form.name.trim(),
        phone:  form.phone.trim(),
      });
      setSavedPayment(res.data?.data || null);
      setSuccess(true);
      if (onSuccess) onSuccess();
    } catch (err) {
      setFormError(err?.response?.data?.message || "Payment failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => { if (!submitting) onClose(); };

  // ─── decide title ──────────────────────────────────────────────────────────
  const titleText = success ? "Payment Recorded!" : alreadyRecorded ? "Exchange Receipt" : "Exchange Payment";
  const titleBg   = success || alreadyRecorded
    ? "linear-gradient(135deg, #1b5e20 0%, #2e7d32 100%)"
    : "linear-gradient(135deg, #bf360c 0%, #e65100 100%)";

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="xs"
      PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}>

      {/* ── TITLE ── */}
      <DialogTitle sx={{
        fontWeight: 800, background: titleBg, color: "#fff",
        display: "flex", alignItems: "center", justifyContent: "space-between",
        py: 1.8, px: 2.5,
      }}>
        <Box display="flex" alignItems="center" gap={1}>
          {success || alreadyRecorded
            ? <CheckCircleIcon sx={{ fontSize: 20 }} />
            : <PaymentIcon     sx={{ fontSize: 20 }} />}
          <Typography fontWeight={800} fontSize="1rem">{titleText}</Typography>
        </Box>
        <IconButton onClick={handleClose} size="small" sx={{ color: "#fff" }} disabled={submitting}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 2.5, bgcolor: "#f8fafc" }}>

        {/* ── LOADING ── */}
        {fetchLoading && (
          <Box textAlign="center" py={6}>
            <CircularProgress size={32} sx={{ color: "#e65100" }} />
            <Typography variant="caption" display="block" mt={1.5} color="text.secondary">
              Loading payment details…
            </Typography>
          </Box>
        )}

        {/* ── FETCH ERROR ── */}
        {!fetchLoading && fetchError && (
          <Box>
            <Alert severity="error" sx={{ borderRadius: 2, mb: 2 }}>{fetchError}</Alert>
            <Button fullWidth variant="outlined" onClick={handleClose} sx={{ borderRadius: 2 }}>Close</Button>
          </Box>
        )}

        {!fetchLoading && !fetchError && (
          <>
            {/* ── Bike card — always shown ── */}
            <Paper elevation={0} sx={{
              p: 1.8, mb: 2, borderRadius: 2,
              border: "1px solid #e0e7ef",
              background: "linear-gradient(135deg, #fff8f5 0%, #fff 100%)",
            }}>
              <Box display="flex" alignItems="center" gap={1} mb={1.2}>
                <TwoWheelerIcon sx={{ color: "#e65100", fontSize: 17 }} />
                <Typography fontWeight={800} fontSize="0.78rem" color="#e65100" letterSpacing={0.3}>
                  Bike Details
                </Typography>
              </Box>
              <Grid container spacing={1}>
                <Grid item xs={6}><Label>Model</Label><Val>{bike?.model}</Val></Grid>
                <Grid item xs={6}><Label>Reg. Number</Label><Val>{bike?.registrationNumber}</Val></Grid>
                <Grid item xs={6}><Label>Owner</Label><Val>{bike?.ownerName}</Val></Grid>
                <Grid item xs={6}><Label>Mobile</Label><Val>{bike?.mobile}</Val></Grid>
                <Grid item xs={6}><Label>Mobile</Label><Val>{bike?.purchasePrice}</Val></Grid>
              </Grid>
            </Paper>

            {/* ══ CASE A: already recorded ══════════════════════════════════ */}
            {!success && alreadyRecorded && (
              <>
                {/* Summary banner */}
                <Paper elevation={0} sx={{
                  p: 2, mb: 2, borderRadius: 2, textAlign: "center",
                  bgcolor: saleDetail.status === "PAID" ? "#e8f5e9" : "#fff3e0",
                  border: `1px solid ${saleDetail.status === "PAID" ? "#a5d6a7" : "#ffb74d"}`,
                }}>
                  {saleDetail.status === "PAID"
                    ? <CheckCircleIcon sx={{ color: "#2e7d32", fontSize: 32 }} />
                    : <PaymentIcon    sx={{ color: "#e65100", fontSize: 32 }} />}
                  <Typography fontWeight={800} mt={0.5}
                    color={saleDetail.status === "PAID" ? "#2e7d32" : "#e65100"} fontSize="0.9rem">
                    {saleDetail.status === "PAID" ? "Payment Completed" : "Partial Payment"}
                  </Typography>
                  <Grid container spacing={1} mt={0.5} textAlign="left">
                    <Grid item xs={4}>
                      <Label>Total</Label>
                      <Val color="#333">₹{(saleDetail.exchangePrice || 0).toLocaleString("en-IN")}</Val>
                    </Grid>
                    <Grid item xs={4}>
                      <Label>Paid</Label>
                      <Val color="#2e7d32">₹{(saleDetail.paidAmount || 0).toLocaleString("en-IN")}</Val>
                    </Grid>
                    <Grid item xs={4}>
                      <Label>Remaining</Label>
                      <Val color={saleDetail.remainingAmount > 0 ? "#c62828" : "#2e7d32"}>
                        ₹{(saleDetail.remainingAmount || 0).toLocaleString("en-IN")}
                      </Val>
                    </Grid>
                  </Grid>
                </Paper>

                {/* Existing payment records */}
                {saleDetail.payments?.length > 0 && (
                  <>
                    <Box display="flex" alignItems="center" gap={0.8} mb={1}>
                      <ReceiptIcon sx={{ color: "#888", fontSize: 14 }} />
                      <Typography variant="caption" fontWeight={700} color="#888" letterSpacing={0.5} textTransform="uppercase">
                        Payment Record
                      </Typography>
                    </Box>
                    {saleDetail.payments.map((p, i) => (
                      <Paper key={i} elevation={0} sx={{
                        p: 1.8, mb: 1, borderRadius: 2,
                        border: "1px solid #dcedc8", bgcolor: "#f9fbe7",
                      }}>
                        <Grid container spacing={1}>
                          <Grid item xs={6}>
                            <Label>Amount Paid</Label>
                            <Typography variant="body1" fontWeight={900} color="#2e7d32">
                              ₹{(p.amount || 0).toLocaleString("en-IN")}
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Label>Date</Label>
                            <Val>{p.date ? new Date(p.date).toLocaleDateString("en-IN") : "—"}</Val>
                          </Grid>
                          {p.name  && <Grid item xs={6}><Label>Recipient</Label><Val>{p.name}</Val></Grid>}
                          {p.phone && <Grid item xs={6}><Label>Phone</Label><Val>{p.phone}</Val></Grid>}
                        </Grid>
                      </Paper>
                    ))}
                  </>
                )}

                <Alert severity="info" sx={{ borderRadius: 2, mb: 2, fontSize: "0.74rem", py: 0.5 }}>
                  Payment has already been recorded. Only one payment per exchange bike is allowed.
                </Alert>

                <Button fullWidth variant="contained" onClick={handleClose}
                  sx={{ borderRadius: 2, fontWeight: 700, bgcolor: "#546e7a", "&:hover": { bgcolor: "#37474f" } }}>
                  Close
                </Button>
              </>
            )}

            {/* ══ CASE B: just submitted ════════════════════════════════════ */}
            {success && (
              <>
                <Paper elevation={0} sx={{
                  p: 2.5, mb: 2, borderRadius: 2, textAlign: "center",
                  bgcolor: "#e8f5e9", border: "1px solid #a5d6a7",
                }}>
                  <CheckCircleIcon sx={{ color: "#2e7d32", fontSize: 44 }} />
                  <Typography fontWeight={800} color="#2e7d32" mt={0.8} fontSize="1rem">
                    Payment Saved!
                  </Typography>
                  <Typography variant="caption" color="#555">
                    Exchange payment has been recorded successfully.
                  </Typography>
                </Paper>

                {savedPayment && (
                  <Paper elevation={0} sx={{
                    p: 2, mb: 2, borderRadius: 2,
                    border: "1px solid #dcedc8", bgcolor: "#f9fbe7",
                  }}>
                    <Box display="flex" alignItems="center" gap={0.8} mb={1.2}>
                      <ReceiptIcon sx={{ color: "#2e7d32", fontSize: 14 }} />
                      <Typography variant="caption" fontWeight={700} color="#2e7d32" letterSpacing={0.5} textTransform="uppercase">
                        Receipt
                      </Typography>
                    </Box>
                    <Grid container spacing={1}>
                      <Grid item xs={6}>
                        <Label>Exchange Price</Label>
                        <Val color="#333">₹{(savedPayment.exchangePrice || 0).toLocaleString("en-IN")}</Val>
                      </Grid>
                      <Grid item xs={6}>
                        <Label>Amount Paid</Label>
                        <Val color="#2e7d32">₹{(savedPayment.paidAmount || 0).toLocaleString("en-IN")}</Val>
                      </Grid>
                      <Grid item xs={6}>
                        <Label>Remaining</Label>
                        <Val color={savedPayment.remainingAmount > 0 ? "#c62828" : "#2e7d32"}>
                          ₹{(savedPayment.remainingAmount || 0).toLocaleString("en-IN")}
                        </Val>
                      </Grid>
                      <Grid item xs={6}>
                        <Label>Status</Label>
                        <Chip label={savedPayment.status} size="small" sx={{
                          mt: 0.3, fontWeight: 800, fontSize: "0.65rem",
                          bgcolor: savedPayment.status === "PAID" ? "#e8f5e9" : "#fff3e0",
                          color:   savedPayment.status === "PAID" ? "#2e7d32" : "#e65100",
                          border:  `1px solid ${savedPayment.status === "PAID" ? "#a5d6a7" : "#ffb74d"}`,
                        }} />
                      </Grid>
                      {savedPayment.invoiceNo && (
                        <Grid item xs={12}><Label>Invoice</Label><Val>{savedPayment.invoiceNo}</Val></Grid>
                      )}
                    </Grid>
                  </Paper>
                )}

                <Button fullWidth variant="contained" onClick={handleClose}
                  sx={{ borderRadius: 2, fontWeight: 700, bgcolor: "#2e7d32", "&:hover": { bgcolor: "#1b5e20" } }}>
                  Done
                </Button>
              </>
            )}

            {/* ══ CASE C: unpaid — show form ════════════════════════════════ */}
            {!success && !alreadyRecorded && (
              <>
                {saleDetail && (
                  <Paper elevation={0} sx={{
                    p: 1.8, mb: 2, borderRadius: 2,
                    bgcolor: "#fff3e0", border: "1px solid #ffb74d", textAlign: "center",
                  }}>
                    <Label>Exchange Price (Agreed Value)</Label>
                    <Typography variant="h5" fontWeight={900} color="#e65100" mt={0.5}>
                      ₹{(saleDetail.exchangePrice || 0).toLocaleString("en-IN")}
                    </Typography>
                  </Paper>
                )}

                <Divider sx={{ mb: 2 }}>
                  <Typography variant="caption" fontWeight={700} color="#888" letterSpacing={0.5}>
                    PAYMENT DETAILS
                  </Typography>
                </Divider>

                <Box display="flex" flexDirection="column" gap={1.8}>
                  <TextField label="Payment Amount (₹)" type="number" size="small" fullWidth
                    value={form.amount}
                    onChange={(e) => setForm((p) => ({ ...p, amount: e.target.value }))}
                    inputProps={{ min: 1 }}
                    InputProps={{ startAdornment: <InputAdornment position="start"><CurrencyRupeeIcon sx={{ fontSize: 15, color: "#e65100" }} /></InputAdornment> }}
                    helperText={saleDetail?.exchangePrice ? `Agreed: ₹${saleDetail.exchangePrice.toLocaleString("en-IN")}` : undefined}
                  />
                  <TextField label="Payment Date" type="date" size="small" fullWidth
                    InputLabelProps={{ shrink: true }}
                    value={form.date}
                    onChange={(e) => setForm((p) => ({ ...p, date: e.target.value }))}
                    InputProps={{ startAdornment: <InputAdornment position="start"><CalendarTodayIcon sx={{ fontSize: 14, color: "#666" }} /></InputAdornment> }}
                  />
                  <TextField label="Recipient Name" size="small" fullWidth
                    value={form.name}
                    onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                    InputProps={{ startAdornment: <InputAdornment position="start"><PersonIcon sx={{ fontSize: 15, color: "#666" }} /></InputAdornment> }}
                  />
                  <TextField label="Phone Number" size="small" fullWidth
                    value={form.phone}
                    onChange={(e) => setForm((p) => ({ ...p, phone: e.target.value }))}
                    InputProps={{ startAdornment: <InputAdornment position="start"><PhoneIcon sx={{ fontSize: 15, color: "#666" }} /></InputAdornment> }}
                  />
                </Box>

                {formError && (
                  <Alert severity="error" sx={{ mt: 2, borderRadius: 2, fontSize: "0.8rem" }}>{formError}</Alert>
                )}

                <Alert severity="warning" sx={{ mt: 2, borderRadius: 2, fontSize: "0.74rem", py: 0.5 }}>
                  ⚠️ Only <strong>one payment</strong> is allowed per exchange bike.
                </Alert>

                <Box display="flex" gap={1.5} mt={2.5}>
                  <Button fullWidth variant="outlined" onClick={handleClose} disabled={submitting}
                    sx={{ borderRadius: 2, fontWeight: 700 }}>
                    Cancel
                  </Button>
                  <Button fullWidth variant="contained" onClick={handleSubmit} disabled={submitting}
                    sx={{ borderRadius: 2, fontWeight: 700, bgcolor: "#e65100", "&:hover": { bgcolor: "#bf360c" } }}>
                    {submitting
                      ? <Box display="flex" alignItems="center" gap={1}><CircularProgress size={14} sx={{ color: "#fff" }} />Saving…</Box>
                      : "✅ Record Payment"}
                  </Button>
                </Box>
              </>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ExchangeSellModal;